import { VIDEO_SAFETY_SECONDS } from '../../../renderer/src/service-policy';

/** Selectable route playback paces measured in video seconds per kilometre. */
export const PLAYBACK_PACE_OPTIONS = [1, 2, 3, 5] as const;

/** Route playback pace measured in video seconds per kilometre. */
export type PlaybackSecondsPerKilometer = (typeof PLAYBACK_PACE_OPTIONS)[number];

/** Default route playback pace for interactive previews and requested renders. */
export const DEFAULT_PLAYBACK_SECONDS_PER_KILOMETER: PlaybackSecondsPerKilometer = 3;

/** Fixed opening and finish time reserved around route traversal. */
export const FILM_BOOKEND_SECONDS = 6;

/** Smallest complete proof accepted by the renderer contract. */
export const MIN_RENDER_DURATION_SECONDS = 5;

/** Smallest paced film, preserving one traversal second between bookends. */
const MIN_PACED_FILM_DURATION_SECONDS = FILM_BOOKEND_SECONDS + 1;

/** Largest complete film accepted to bound render cost and output size. */
export const MAX_RENDER_DURATION_SECONDS = VIDEO_SAFETY_SECONDS;

/** Fallback used only when an isolated render URL supplies an invalid duration. */
export const DEFAULT_RENDER_DURATION_SECONDS = 45;

/**
 * Tests whether a numeric value is one of the supported route playback paces.
 *
 * @param candidate - Candidate seconds-per-kilometre value.
 * @returns Whether the candidate is available in the playback control.
 */
export function isPlaybackSecondsPerKilometer(
  candidate: number,
): candidate is PlaybackSecondsPerKilometer {
  return PLAYBACK_PACE_OPTIONS.some((pace) => pace === candidate);
}

/**
 * Calculates a bounded film duration from route distance and selected pace.
 *
 * @param distanceKilometers - Complete route distance in kilometres.
 * @param secondsPerKilometer - Supported number of video seconds per kilometre.
 * @returns Whole film seconds including the fixed opening and finish.
 * @throws TypeError when distance is not finite or the pace is unsupported.
 * @throws RangeError when distance is negative.
 */
export function playbackDurationSeconds(
  distanceKilometers: number,
  secondsPerKilometer: PlaybackSecondsPerKilometer,
): number {
  if (!Number.isFinite(distanceKilometers)) {
    throw new TypeError("Route distance must be finite.");
  }
  if (distanceKilometers < 0) {
    throw new RangeError("Route distance must not be negative.");
  }
  if (!isPlaybackSecondsPerKilometer(secondsPerKilometer)) {
    throw new TypeError("Route playback pace is unsupported.");
  }
  const requested = Math.ceil(
    FILM_BOOKEND_SECONDS + distanceKilometers * secondsPerKilometer,
  );
  return Math.min(
    MAX_RENDER_DURATION_SECONDS,
    Math.max(MIN_PACED_FILM_DURATION_SECONDS, requested),
  );
}

/**
 * Provides a concise label for a supported route playback pace.
 *
 * @param secondsPerKilometer - Supported route playback pace.
 * @returns Human-readable pace label.
 */
export function playbackPaceLabel(
  secondsPerKilometer: PlaybackSecondsPerKilometer,
): string {
  if (!isPlaybackSecondsPerKilometer(secondsPerKilometer)) {
    throw new TypeError("Route playback pace is unsupported.");
  }
  return `${secondsPerKilometer} video sec/km`;
}
